/**
 * draftModel — the vocabulary of a scratchpad draft.
 *
 * A draft is an ordinary `files` row (type 'doc', category 'Drafts'). Everything
 * that makes it a draft rather than a document lives in this file: how its
 * markdown maps to editable blocks, how columns are marked, how it gets named.
 *
 * ── Why blocks are state and the string is derived ───────────────────────────
 * The editor holds `columns: [[{id, text}, …], …]` and computes the markdown
 * from it. It does NOT re-parse the string on every keystroke. Parsing on each
 * change would re-key the block array as you type, remounting the focused
 * <textarea> and throwing the caret to the end of the line every character.
 *
 * `parse` therefore runs exactly twice in a draft's life: when it loads, and
 * when multi-block content is pasted into it.
 *
 * ── Columns ──────────────────────────────────────────────────────────────────
 * A column break is an HTML comment on its own line. That choice matters: the
 * body still opens correctly in Monaco, renders as a normal document in
 * Preview.js, survives download, and costs ~18 characters of the 3000-char
 * budget chat.py allows a file in an agent prompt.
 *
 * The column *count* is not stored anywhere — it is the number of breaks plus
 * one. One less piece of metadata to keep honest, and a hand-edited body can
 * never disagree with its own header.
 */

// ── Constants ────────────────────────────────────────────────────────────────

export const DRAFTS_CATEGORY = 'Drafts';
export const DRAFT_TAG = 'draft';
export const DRAFT_ATTACHMENT_TAG = 'draft-attachment';

/** Sits below the seeded project docs (priority 7-10) in the artifact sort. */
export const DRAFT_PRIORITY = 3;

/**
 * "Give me a blank one", as distinct from `null` — which means "whatever I had
 * last, or the newest draft if I've never opened this". The dock needs to tell
 * those apart, and the distinction has to survive a reload, so it rides in the
 * same persisted field rather than a second flag.
 *
 * Every request for a blank draft mints its own id (`__new__:7`) rather than
 * reusing the bare sentinel. The dock loads a draft whenever the active id
 * *changes*, so a distinct id per request is what makes "new draft" work when
 * one is already on screen — including the case that matters most, a shortcut
 * pressed while an unsaved note is still in the editor. With a single shared
 * sentinel nothing changed, so nothing happened.
 */
export const NEW_DRAFT = '__new__';

let newSeq = 0;
export const newDraftId = () => `${NEW_DRAFT}:${(newSeq += 1)}`;
export const isNewDraftId = (id) =>
    id == null || id === NEW_DRAFT || (typeof id === 'string' && id.startsWith(`${NEW_DRAFT}:`));

export const COLUMN_MARKER = '<!--forge:col-->';
const COLUMN_SPLIT = /^<!--forge:col-->[ \t]*$/m;

/** Three is the ceiling: a fourth column on a laptop is narrower than a line. */
export const MAX_COLUMNS = 3;

/**
 * Dock width per column count. Picking more columns widens the panel — the
 * alternative is three columns sharing 440px, which is unreadable.
 */
export const DOCK_WIDTH = { 1: 440, 2: 660, 3: 880 };

/**
 * Container metrics shared by both halves of a block. The rendered view and the
 * raw textarea MUST agree on these or focusing a block nudges everything below
 * it — the one flaw that makes live preview feel broken rather than magic.
 *
 * The horizontal inset used to live here too. It moved to the column, because a
 * block that owns its own left padding has nowhere to put a hover gutter: the
 * gutter has to sit *outside* the text and still inside the panel.
 */
export const BLOCK_PAD = 'py-[3px]';
export const BLOCK_TEXT = 'text-[13px] leading-[1.7]';

/**
 * The column's own inset. The extra 32px on the left of a desktop column is not
 * decoration — it is exactly where the drag handle and the + button live, and
 * they are absolutely positioned into it so revealing them costs no layout.
 * A phone has no hover, so it gets a symmetric inset and the space back.
 */
export const COLUMN_PAD = 'px-3 py-2 md:pl-8 md:pr-3';

/** One rendered line of body text, to the pixel. Used to size an empty block. */
export const LINE_HEIGHT = 22;

/** How images inside a draft point at their asset row. */
export const ASSET_PROTOCOL = 'forge-asset://';

export const assetUrl = (fileId) => `${ASSET_PROTOCOL}${fileId}`;
export const assetIdFromUrl = (url) =>
    (url || '').startsWith(ASSET_PROTOCOL)
        // Everything after the `#` is the editor's, not the id's — see below.
        ? url.slice(ASSET_PROTOCOL.length).split('#')[0]
        : null;

// ── Image width ──────────────────────────────────────────────────────────────

/**
 * A resized image carries its width in a fragment on its own src:
 *
 *     ![shot.webp](forge-asset://a1b2c3#w=480)
 *
 * ── Why the fragment and not the title slot ──────────────────────────────────
 * `forge-asset://` is already a protocol only this app can resolve, so an image
 * block is already meaningless outside it — putting forge-only metadata inside a
 * forge-only URL costs nothing that was not already spent. The obvious
 * alternative, markdown's title slot (`![alt](src "w=480")`), squats on a field
 * the user can actually see: it becomes a tooltip in Preview.js and in every
 * other renderer the body ever passes through.
 *
 * The number is a pixel width and it is always rendered against `max-w-full`, so
 * a width chosen in a one-column dock simply clamps in a three-column one and
 * springs back on the way out. An image with no fragment is not "sized 0", it is
 * unsized, and keeps the default height cap it has always had — which is what
 * makes this need no migration.
 */

const IMAGE_TOKEN = /^\s*!\[([^\]]*)\]\(\s*(\S+?)\s*\)\s*$/;
const WIDTH_FRAGMENT = /#w=(\d+)$/;

/** The pixel width encoded in an image src, or null when it carries none. */
export function imageWidthFromSrc(src) {
    const m = (src || '').match(WIDTH_FRAGMENT);
    return m ? Number(m[1]) : null;
}

/**
 * A lone-image block broken into its parts, or null when the text is anything
 * else. Deliberately stricter than `blockKind`'s `IMAGE_ONLY`: this one has to
 * hand back a src it is willing to rewrite.
 */
export function parseImageBlock(text) {
    const m = (text || '').match(IMAGE_TOKEN);
    if (!m) return null;
    return { alt: m[1], src: m[2], width: imageWidthFromSrc(m[2]) };
}

/**
 * The same block with its width set, replaced, or — for a null width — removed.
 * Returns the text unchanged when it is not a lone image, so a caller never has
 * to check twice.
 */
export function withImageWidth(text, width) {
    const parsed = parseImageBlock(text);
    if (!parsed) return text;
    const bare = parsed.src.replace(WIDTH_FRAGMENT, '');
    const src = width ? `${bare}#w=${Math.round(width)}` : bare;
    return `![${parsed.alt}](${src})`;
}

// ── Block identity ───────────────────────────────────────────────────────────

// Module-scope so ids stay unique across every draft opened in a session. React
// keys only need to be stable within a list, but a global counter also means an
// id can never be reused by a different block after a switch.
let seq = 0;
const nextId = () => `b${(seq += 1)}`;

export const makeBlock = (text = '') => ({ id: nextId(), text });

// ── Block classification ─────────────────────────────────────────────────────

const FENCE = /^\s{0,3}(```|~~~)/;
const HEADING = /^\s{0,3}#{1,6}\s/;
const LIST = /^\s*([-*+]|\d+[.)])\s/;
const QUOTE = /^\s{0,3}>/;
const TABLE = /^\s{0,3}\|/;
const RULE = /^\s{0,3}(-{3,}|\*{3,}|_{3,})\s*$/;
const IMAGE_ONLY = /^\s*!\[[^\]]*\]\([^)]*\)\s*$/;

/**
 * What kind of thing a block is. Drives both the renderer and the keyboard
 * model — a heading ends on Enter, a fence swallows it.
 */
export function blockKind(text) {
    const first = (text || '').split('\n', 1)[0];
    if (FENCE.test(first)) return 'fence';
    if (IMAGE_ONLY.test(text || '')) return 'image';
    if (HEADING.test(first)) return 'heading';
    if (TABLE.test(first)) return 'table';
    if (QUOTE.test(first)) return 'quote';
    if (LIST.test(first)) return 'list';
    if (RULE.test(first)) return 'rule';
    return 'paragraph';
}

/** How deep a heading is, or 0 when the block is not one. */
export function headingLevel(text) {
    const m = (text || '').split('\n', 1)[0].match(/^\s{0,3}(#{1,6})\s/);
    return m ? m[1].length : 0;
}

/**
 * The space a block claims above itself.
 *
 * ── Why spacing is per-kind and lives on the row ─────────────────────────────
 * A uniform gap between blocks is what made this read as a stack of cards: every
 * row equally far from its neighbours is a list, not a document. Prose wants its
 * paragraphs nearly touching — 2px on a 22px line box reads as continuous flow —
 * and its headings standing well clear of what came before. That difference is
 * the whole of what makes a page scan as sections.
 *
 * It is applied to the row rather than inside either half of the block, so the
 * raw textarea and the rendered view are spaced identically by construction.
 * That is the height contract: it cannot drift, because there is only one of it.
 */
const GAP = {
    paragraph: 'mt-[2px]',
    list: 'mt-[2px]',
    quote: 'mt-[6px]',
    image: 'mt-[10px]',
    table: 'mt-[10px]',
    fence: 'mt-[10px]',
    rule: 'mt-[14px]',
};

const HEADING_GAP = {
    1: 'mt-[24px]', 2: 'mt-[18px]', 3: 'mt-[14px]',
    4: 'mt-[12px]', 5: 'mt-[12px]', 6: 'mt-[12px]',
};

export function blockGapClass(text, kind) {
    return kind === 'heading'
        ? (HEADING_GAP[headingLevel(text)] || 'mt-[14px]')
        : (GAP[kind] || 'mt-[2px]');
}

// A line that starts a new block even without a blank line before it. CommonMark
// lets headings, fences, quotes and lists interrupt a paragraph; plain text
// does not.
//
// `run` is the kind of the block already in progress, and it is the whole point
// of this signature: a table row does not interrupt a table, and a quote line
// does not interrupt a quote. Reading them as interrupts is what used to stop
// every multi-line run on its own second line — a pasted table came apart into
// one block per row, and serialising put blank lines between those rows, so the
// table was destroyed on disk as well as on screen.
const interrupts = (line, run) =>
    HEADING.test(line)
    || FENCE.test(line)
    || RULE.test(line)
    || (run !== 'quote' && QUOTE.test(line))
    || (run !== 'table' && TABLE.test(line));

/**
 * A GFM alignment row — the `| --- | :---: |` line under a table's header.
 *
 * Pipes, colons, dashes and space only, and at least one dash: that last part
 * is what stops `|  |  |`, an empty first row, from reading as a delimiter.
 */
export const isTableDelimiterRow = (line) =>
    !!line && TABLE.test(line) && /^[\s|:-]+$/.test(line) && line.includes('-');

/**
 * Split one column's markdown into block texts.
 *
 * Deliberately not a full CommonMark parser: it only has to agree with itself,
 * because whatever it produces is re-serialised with blank lines between blocks
 * and would parse back identically.
 */
export function parseBlocks(markdown) {
    const lines = (markdown || '').split('\n');
    const out = [];
    let i = 0;

    while (i < lines.length) {
        if (!lines[i].trim()) {
            i += 1;
            continue;
        }

        const start = i;
        const head = lines[i];
        // Asking the classifier rather than re-testing the regexes here is what
        // guarantees the grouper and `blockKind` can never disagree about what
        // this block is.
        const run = blockKind(head);

        if (FENCE.test(head)) {
            // Closing fence must match the opener's character, so ``` inside a
            // ~~~ block doesn't terminate it early.
            const token = head.trim().slice(0, 3);
            i += 1;
            while (i < lines.length && !lines[i].trim().startsWith(token)) i += 1;
            if (i < lines.length) i += 1; // consume the closing fence
        } else if (HEADING.test(head) || RULE.test(head)) {
            i += 1; // single-line by definition
        } else if (run === 'list' || run === 'quote' || run === 'table') {
            // Runs to the next blank line. Lazy continuation means a bare line
            // after a list item still belongs to that item.
            i += 1;
            while (i < lines.length && lines[i].trim() && !interrupts(lines[i], run)) i += 1;
        } else {
            i += 1;
            while (i < lines.length && lines[i].trim() && !interrupts(lines[i], run) && !LIST.test(lines[i])) i += 1;
        }

        out.push(lines.slice(start, i).join('\n').replace(/\s+$/, ''));
    }

    return out;
}

/**
 * Undo the damage the old grouper wrote to disk.
 *
 * Before `interrupts` learned about runs, every table row became its own block,
 * and `serializeDraft` put a blank line between blocks — so a saved draft holds
 * a table with blank lines between its rows, which is no longer a table at all.
 * Fixing the parser does not fix those files: the blank lines are really there,
 * and they re-shred identically on every load.
 *
 * The delimiter row is what makes this safe to do automatically. A shredded
 * table reads `row, blank, row, blank, row` with the alignment row somewhere in
 * the middle; two tables the user genuinely wrote next to each other read
 * `…last row, blank, header, delimiter…`. So: close a gap between two table
 * lines only when the line *after* the gap is not itself followed by a
 * delimiter. That one lookahead separates the two cases exactly.
 *
 * Tables only. A quote run has no delimiter to key on, so "shredded quote" and
 * "two adjacent one-line quotes" are indistinguishable — and separate quote
 * blocks still render fine, where a table does not. Not worth guessing at
 * someone's content for a cosmetic gain.
 */
function healShreddedTables(markdown) {
    if (!markdown || !markdown.includes('|')) return markdown;

    const lines = markdown.split('\n');
    const out = [];

    for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i];
        if (line.trim()) { out.push(line); continue; }

        // Walk the whole blank run at once, so `row\n\n\nrow` heals too.
        let j = i;
        while (j < lines.length && !lines[j].trim()) j += 1;

        const prev = out[out.length - 1];
        const next = lines[j];
        const after = lines[j + 1];

        const joins = prev && next
            && TABLE.test(prev) && TABLE.test(next)
            && !isTableDelimiterRow(after);

        if (!joins) out.push('');
        i = j - 1;
    }

    return out.join('\n');
}

// ── Document ⇄ model ─────────────────────────────────────────────────────────

/**
 * Markdown → `[[{id, text}, …], …]`, one inner array per column.
 *
 * Always returns at least one column, and every column holds at least one block
 * — an empty column with nothing to click would be a dead zone you couldn't
 * type into.
 */
export function parseDraft(content) {
    const chunks = (content || '').split(COLUMN_SPLIT).slice(0, MAX_COLUMNS);
    const columns = (chunks.length ? chunks : ['']).map((chunk) => {
        // Healing belongs here and not in `parseBlocks`, which paste also uses:
        // pasting two tables one after another must leave them as two tables.
        // Only a file being opened can have been shredded by the old grouper.
        const blocks = parseBlocks(healShreddedTables(chunk)).map(makeBlock);
        return blocks.length ? blocks : [makeBlock('')];
    });
    return columns;
}

/**
 * `[[{id, text}, …], …]` → markdown.
 *
 * Empty blocks are dropped, but empty *columns* are kept: "three columns, the
 * third still to fill" is a layout the user chose, and trimming it would silently
 * undo their click the moment the draft round-trips.
 */
export function serializeDraft(columns) {
    return columns
        .map((blocks) =>
            blocks
                .map((b) => b.text.replace(/\s+$/, ''))
                .filter(Boolean)
                .join('\n\n')
        )
        .join(`\n\n${COLUMN_MARKER}\n\n`);
}

/** True when the draft holds nothing but whitespace — used for cleanup on close. */
export function isEmptyDraft(columns) {
    return !columns.some((blocks) => blocks.some((b) => b.text.trim()));
}

/**
 * Grow or shrink the column count.
 *
 * Shrinking folds the removed columns' blocks into the last surviving one rather
 * than deleting them. Losing paragraphs to a layout toggle is not a trade any
 * user would accept, and there is no undo for it.
 */
export function setColumnCount(columns, n) {
    const target = Math.max(1, Math.min(MAX_COLUMNS, n));
    if (target === columns.length) return columns;

    if (target > columns.length) {
        const next = columns.slice();
        while (next.length < target) next.push([makeBlock('')]);
        return next;
    }

    const kept = columns.slice(0, target);
    const folded = columns.slice(target).flat().filter((b) => b.text.trim());
    if (!folded.length) return kept;

    const last = kept[target - 1].filter((b) => b.text.trim());
    kept[target - 1] = [...last, ...folded];
    return kept;
}

// ── Naming ───────────────────────────────────────────────────────────────────

const SYNTAX = /^[\s>#*_`~\-+[\]()!|]+|[\s>#*_`~[\]()|]+$/g;

/**
 * A filename from the first line of the body, so a draft you never title still
 * reads as something in the switcher and the artifact list.
 *
 * Auto-naming stops as soon as the user renames the draft themselves — the dock
 * compares the stored name against the last value this produced, and a mismatch
 * means the name is theirs now.
 */
export function deriveDraftName(content, now = new Date()) {
    const firstLine = (content || '')
        .split('\n')
        .map((l) => l.replace(COLUMN_MARKER, '').trim())
        .find((l) => l && !FENCE.test(l) && !RULE.test(l));

    const slug = (firstLine || '')
        .replace(SYNTAX, '')
        .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 48)
        .replace(/-+$/, '');

    if (slug) return `${slug}.md`;

    const pad = (n) => String(n).padStart(2, '0');
    const month = now.toLocaleString('en-US', { month: 'short' });
    return `draft-${month}-${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}.md`;
}

/** The name shown in the dock header and switcher — the filename minus its extension. */
export const draftLabel = (file) => (file?.name || 'Untitled').replace(/\.md$/i, '');

// ── Keyboard helpers ─────────────────────────────────────────────────────────

/**
 * A selector matching everything inside a block that is a control rather than
 * text: links, an image's toolbar and handles, a table's grid and its grips.
 *
 * Every gesture that turns a rendered block into a caret has to stand down for
 * these, and there are enough of those places now that a new block type with
 * chrome of its own would certainly be wired into some of them and not others.
 * One selector, imported everywhere, is how that stops being possible.
 */
export const BLOCK_UI = 'a, [data-image-ui], [data-table-ui]';

/**
 * ⌘⌥A / Ctrl+Alt+A — select every block, whatever the layout did to the letter.
 *
 * `key` alone is not enough: macOS turns Option+A into 'å', and browsers do not
 * agree on whether Command held alongside restores the plain letter. `code`
 * alone is not enough either — it is the physical QWERTY position, so on AZERTY
 * `KeyA` is the key labelled Q. Accepting either is the only thing that is right
 * on both, and the cost of being generous here is nil: nothing else in the
 * editor wants Option.
 *
 * ⌘⇧A would read more naturally, but Chrome binds it to Search Tabs and Firefox
 * to the add-ons manager, and a page cannot preventDefault either.
 */
export function isSelectAllChord(e) {
    if (!(e.metaKey || e.ctrlKey) || !e.altKey) return false;
    return e.code === 'KeyA' || e.key === 'a' || e.key === 'A' || e.key === 'å' || e.key === 'Å';
}

/**
 * What Enter should insert to continue the list the caret is sitting in, or
 * `null` if there is no list to continue.
 *
 * Returns `''` for a bare marker with no content ("- " alone), which the caller
 * reads as "the user is done with this list" and uses to strip the marker
 * instead of adding another one.
 */
export function listContinuation(line) {
    const bullet = line.match(/^(\s*)([-*+])\s+(\[[ xX]\]\s+)?(.*)$/);
    if (bullet) {
        const [, indent, mark, task, rest] = bullet;
        if (!rest.trim()) return '';
        return `${indent}${mark} ${task ? '[ ] ' : ''}`;
    }

    const ordered = line.match(/^(\s*)(\d+)([.)])\s+(.*)$/);
    if (ordered) {
        const [, indent, num, delim, rest] = ordered;
        if (!rest.trim()) return '';
        return `${indent}${Number(num) + 1}${delim} `;
    }

    return null;
}

/**
 * Map an offset in a block's *rendered* text back to an offset in its markdown
 * source, so clicking the middle of a paragraph puts the caret there rather than
 * at the end.
 *
 * Walks the source counting only characters that survive rendering: markdown
 * punctuation at a line's head (`#`, `>`, `- `) and inline emphasis/code runs
 * are skipped. It is a heuristic — link targets and image URLs make an exact
 * mapping impossible without a full AST — so callers must tolerate being a few
 * characters off, and it clamps rather than failing.
 */
/**
 * Where in a rendered node a click landed, as a character offset into its text.
 * Returns null when the browser can't resolve the point, in which case the
 * caller falls back to the end.
 *
 * Lives here rather than in DraftBlock because two components need it now — a
 * whole block, and a single table cell — and DraftBlock imports the table grid,
 * so keeping it there would have made the two modules import each other. It is
 * `sourceOffsetFor`'s other half in any case: this measures the rendered text,
 * that maps the result back onto the markdown behind it.
 */
export function renderedOffsetAt(root, x, y) {
    let node = null;
    let offset = 0;

    if (document.caretRangeFromPoint) {
        const range = document.caretRangeFromPoint(x, y);
        if (!range) return null;
        node = range.startContainer;
        offset = range.startOffset;
    } else if (document.caretPositionFromPoint) {
        const pos = document.caretPositionFromPoint(x, y);
        if (!pos) return null;
        node = pos.offsetNode;
        offset = pos.offset;
    } else {
        return null;
    }

    if (!node || !root.contains(node)) return null;

    // Sum the text ahead of the hit node. Clicking a block's padding resolves to
    // an element rather than a text node, so the walk falls through to the total
    // and the caret lands at the end — which is the right answer for a click
    // below the last line.
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let total = 0;
    while (walker.nextNode()) {
        if (walker.currentNode === node) return total + offset;
        total += walker.currentNode.nodeValue.length;
    }
    return total;
}

export function sourceOffsetFor(source, renderedOffset) {
    if (!renderedOffset || renderedOffset < 0) return 0;

    let visible = 0;
    let i = 0;
    let atLineStart = true;

    while (i < source.length && visible < renderedOffset) {
        const ch = source[i];

        if (ch === '\n') {
            atLineStart = true;
            visible += 1;
            i += 1;
            continue;
        }

        if (atLineStart) {
            const rest = source.slice(i);
            const prefix = rest.match(/^(\s*(?:#{1,6}\s+|>\s?|[-*+]\s+(?:\[[ xX]\]\s+)?|\d+[.)]\s+))/);
            if (prefix) {
                i += prefix[0].length;
                atLineStart = false;
                continue;
            }
            atLineStart = false;
        }

        // Inline emphasis and code markers render to nothing.
        const marker = source.slice(i).match(/^(\*{1,3}|_{1,3}|`+|~~)/);
        if (marker) {
            i += marker[0].length;
            continue;
        }

        visible += 1;
        i += 1;
    }

    return Math.min(i, source.length);
}
